import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { TextZoom } from '@capacitor/text-zoom';

/**
 * Follows the device text size (iOS Larger Text, Android Font size) by scaling the
 * root font-size. The type scale is rem, so the root carries it everywhere.
 *
 * Android recreates the activity when the setting changes, so boot covers it there.
 * iOS keeps the app alive, so the `resume` listener picks the change up.
 */
async function applyTextScale(): Promise<void> {
  // An OTA bundle on an iOS build that predates the plugin still reports it available
  // (its JS shim is registered), but the native call rejects. Keep the default size.
  try {
    const { value } = await TextZoom.getPreferred();
    document.documentElement.style.fontSize = `${Math.round(value * 100)}%`;
  } catch {
    /* default size */
  }
}

export async function initTextScale(): Promise<void> {
  // 1. Browser build, or an Android build without the plugin. Browsers honour rem already.
  if (!Capacitor.isPluginAvailable('TextZoom')) return;

  // 2. Android's WebView applies the system font scale on its own. Reset it to 100%
  //    so only the root font-size scales and nothing doubles.
  if (Capacitor.getPlatform() === 'android') await TextZoom.set({ value: 1 });

  // 3. Apply now and every time the app returns to the foreground.
  await applyTextScale();
  void App.addListener('resume', () => void applyTextScale());
}
